import React, { useEffect, useState } from "react";
import { Button, Select, Space, Col, Row, Tabs } from "antd";
import { PlusOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import styles from "./create-routine.module.css";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../../common/custom-button/custom-button";
import CustomTextfield from "../../../../../common/input/custom-textfield/custom-textfield";
import { fetchSubjectsAcademicView } from "../../../../../services/subject";
import { fetchSections } from "../../../../../services/student";
import { fetchTeacherList } from "../../../../../services/teachers";
import { createAntdSelection } from "../../../../../utils/common";
import {
  createRoutine,
  fetchAcademicYear,
  editRoutine as EditRoutineApi,
} from "../../../../../services/class_routine";
import { toast } from "react-toastify";
import { fetchClassrooms } from "../../../../../services/classrooms";
import { generateTimeSlots } from "../../../../../constants/common";

const { Option } = Select;
const { TabPane } = Tabs;

const days = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

const CreateRoutine = ({
  open,
  onClose,
  editRoutine,
  levelInfo,
  getInstitutionSummary,
}) => {
  const { control, handleSubmit, trigger, watch, reset, getValues } = useForm({
    mode: "onChange",
  });
  const [activeDay, setActiveDay] = useState("Monday");

  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClassroom, setLoadingClassroom] = useState(false);

  const [periods, setPeriods] = useState(
    Object.fromEntries(days.map((day) => [day, []]))
  );

  const watchForClassroom = watch("form_name");

  const fetchRoutineInfo = async () => {
    try {
      setLoading(true);
      const [subjectsResp, classrooms, teachers] = await Promise.all([
        fetchSubjectsAcademicView(),
        fetchClassrooms(),
        fetchTeacherList(),
      ]);

      const classroomData = classrooms?.data?.data?.classrooms;
      const subjects = subjectsResp.data?.data?.finalResult?.reduce(
        (acc, item) => {
          return acc.concat(createAntdSelection(item?.subjects));
        },
        []
      );

      const teachersData = teachers?.data?.data?.map((i) => ({
        label: i?.full_name,
        value: i?.id,
      }));

      setSubjects(subjects);
      setTeacherList(teachersData);
      setClassrooms(createAntdSelection(classroomData));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutineInfo();
    if (editRoutine) {
      reset({ ...editRoutine });
      const obj = {};
      days.forEach((i) => {
        obj[i] = Array.from(
          { length: editRoutine[i]?.length ?? 0 },
          () => ({})
        );
      });

      setPeriods(obj);
    } else {
      setPeriods(Object.fromEntries(days.map((day) => [day, []])));
      reset({ section_name: levelInfo?.classroom_id });
    }
  }, [editRoutine, levelInfo?.classroom_id]);

  useEffect(() => {
    if (watchForClassroom) {
      setLoadingClassroom(true);
      setClassrooms([]);
      fetchSections(watchForClassroom)
        .then(async (resp) => {
          const data = resp?.data?.data?.classrooms;
          setClassrooms(createAntdSelection(data));
        })
        .catch(console.log)
        .finally(() => setLoadingClassroom(false));
    }
  }, [watchForClassroom]);

  const handleAddPeriod = () => {
    setPeriods((prev) => ({
      ...prev,
      [activeDay]: [...prev[activeDay], {}],
    }));
  };

  const handleRemovePeriod = (day, index) => {
    const getValue = getValues();
    const item = getValue?.[day];
    const newRoutine = item?.filter((_, idx) => idx !== index);
    const buildObject = { ...getValue, [day]: newRoutine };
    reset(buildObject);
    setPeriods((prev) => ({
      ...prev,
      [day]: prev[day].filter((_, i) => i !== index),
    }));
  };

  const renderPeriodInputs = (day) =>
    periods[day]?.map((_, index) => (
      <Row
        key={index}
        style={{
          display: "flex",
          marginBottom: 12,
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Col span={6} className={styles.tabInput}>
          <label className={styles.label_styles}>
            Subject<span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name={`${day}.${index}.subject`}
            control={control}
            rules={{ required: "Subject is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                placeholder="Select Subject"
                size="large"
                status={error ? "error" : ""}
                optionFilterProp="label"
                filterOption={(input, option) => {
                  return option?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase());
                }}
                showSearch={true}
              >
                {subjects.map((subject) => (
                  <Option key={subject.value} value={subject.value}>
                    {subject.label}
                  </Option>
                ))}
              </Select>
            )}
          />
        </Col>

        <Col span={6} className={styles.tabInput}>
          <label className={styles.label_styles}>
            Teacher<span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name={`${day}.${index}.teacher`}
            control={control}
            rules={{ required: "Teacher is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                placeholder="Select Teacher"
                size="large"
                optionFilterProp="label"
                filterOption={(input, option) => {
                  return option?.children
                    ?.toLowerCase()
                    .includes(input.toLowerCase());
                }}
                status={error ? "error" : ""}
                showSearch
              >
                {teacherList.map((teacher) => (
                  <Option key={teacher.value} value={teacher.value}>
                    {teacher.label}
                  </Option>
                ))}
              </Select>
            )}
          />
        </Col>

        <Col span={5} className={styles.tabInput}>
          <label className={styles.label_styles}>
            Start Time<span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name={`${day}.${index}.startTime`}
            control={control}
            rules={{ required: "Start time is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                placeholder="Start Time"
                size="large"
                status={error ? "error" : ""}
                suffixIcon={<ClockCircleOutlined />}
              >
                {generateTimeSlots().map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            )}
          />
        </Col>

        <Col span={5} className={styles.tabInput}>
          <label className={styles.label_styles}>
            End Time<span style={{ color: "red" }}>*</span>
          </label>
          <Controller
            name={`${day}.${index}.endTime`}
            control={control}
            rules={{ required: "End time is required" }}
            render={({ field, fieldState: { error } }) => (
              <Select
                {...field}
                placeholder="End Time"
                size="large"
                status={error ? "error" : ""}
                suffixIcon={<ClockCircleOutlined />}
              >
                {generateTimeSlots().map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            )}
          />
        </Col>

        <div
          style={{
            width: "14px",
            height: "3px",
            display: "flex",
            marginTop: "24px",
            backgroundColor: "rgb(184, 37, 17)",
            borderRadius: "20px",
            alignItems: "center",
            border: "none",
            cursor: "pointer",
          }}
          onClick={() => handleRemovePeriod(day, index)}
        ></div>
      </Row>
    ));

  const onSubmit = async (value) => {
    try {
      const isValid = await trigger();
      if (isValid) {
        setLoading(true);

        const academicYear = await fetchAcademicYear();
        const yearId = academicYear.data?.data?.academicYears?.[0]?.id;
        let schedules = [];

        days.forEach((day) => {
          const routine = value[day];
          const daySchedules = routine?.map((period) => {
            return {
              classroom_id: value.section_name,
              subject_info: [
                {
                  subject_id: period?.subject,
                  teacher_id: period?.teacher,
                },
              ],
              start_time: period?.startTime,
              end_time: period?.endTime,
              day,
              academic_year_id: yearId,
            };
          });
          schedules = schedules.concat(daySchedules ?? []);
        });

        const payload = {
          routine_name: value.routine_name,
          schedules,
        };

        const values = getValues();
        if (editRoutine) await EditRoutineApi(values?.routine_id, payload);
        else await createRoutine(payload);
        await getInstitutionSummary();
        onClose();
        toast.success("Route is created successfully!");
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to add routine");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomDrawer
      title="Create Routine"
      open={open}
      onClose={onClose}
      className={styles.drawer}
      loading={loading}
      width="80vw"
      drawerStyles={{ body: { padding: "24px" } }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.formSectionBox}>
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            <Row gutter={[16, 16]}>
              <Col span={8} className={styles.tabInput}>
                <label className={styles.label_styles}>
                  Routine Name<span style={{ color: "red" }}>*</span>
                </label>
                <Controller
                  name="routine_name"
                  control={control}
                  rules={{ required: "Routine name is required" }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomTextfield
                      labelColor="#171717"
                      showSearch
                      size="large"
                      value={value}
                      placeholder="Routine Name"
                      onChange={onChange}
                      status={error ? "error" : ""}
                    />
                  )}
                />
              </Col>
              {/* <Col span={8} className={styles.tabInput}>
                <label className={styles.label_styles}>
                  Form Name<span style={{ color: "red" }}>*</span>
                </label>
                <Controller
                  name="form_name"
                  control={control}
                  // rules={{ required: "Form Name is required" }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomSelect
                      labelColor="#171717"
                      placeholder="--Select--"
                      showSearch
                      size="large"
                      value={value}
                      disabled
                      onChange={onChange}
                      status={error ? "error" : ""}
                    >
                      {classes.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </CustomSelect>
                  )}
                />
              </Col> */}
              <Col span={8} className={styles.tabInput}>
                <label className={styles.label_styles}>
                  Section Name<span style={{ color: "red" }}>*</span>
                </label>
                <Controller
                  name="section_name"
                  control={control}
                  rules={{ required: "Section name is required" }}
                  render={({
                    field: { onChange, value },
                    fieldState: { error },
                  }) => (
                    <CustomSelect
                      labelColor="#171717"
                      placeholder="--Select--"
                      showSearch
                      loading={loadingClassroom}
                      size="large"
                      disabled
                      value={value}
                      onChange={onChange}
                      status={error ? "error" : ""}
                    >
                      {classrooms.map((option) => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </CustomSelect>
                  )}
                />
              </Col>
            </Row>
            <Tabs
              defaultActiveKey="Monday"
              activeKey={activeDay}
              onChange={(key) => setActiveDay(key)}
              tabBarExtraContent={{
                right: (
                  <CustomButton
                    title="Add Period"
                    type="text"
                    onClick={handleAddPeriod}
                    icon={
                      <div className={styles.iconCircle}>
                        <PlusOutlined />
                      </div>
                    }
                    className={styles.addSubjectBtn}
                    showTooltip={false}
                  />
                ),
              }}
            >
              {days.map((day) => (
                <TabPane tab={day} key={day} forceRender>
                  {renderPeriodInputs(day)}
                </TabPane>
              ))}
            </Tabs>
          </Space>
        </div>

        <div className={styles.formActions}>
          <Space
            style={{
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              style={{
                background: "rgba(57, 181, 74, 1)",
                borderColor: "rgba(57, 181, 74, 1)",
              }}
            >
              Save
            </Button>
          </Space>
        </div>
      </form>
    </CustomDrawer>
  );
};

export default CreateRoutine;
